import {
  IconCheckmark,
  IconShieldCheck,
  IconXMark,
  Button
} from '@sequencehq/core-components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import { NodeTimeline, useNodeTimelineVariant } from 'Automations/components'
import { useAllUsers } from 'common/hooks/useUsers'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { useSetAtom } from 'jotai'
import {
  findReviewableTask,
  resolveUserEmails
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { useCurrentUser } from 'common/hooks'
import { ContractPricingPreview } from 'Automations/nodeDefinitions/definitions/action/actionContractExtraction/ExtractedBillingScheduleDataTimelineItem'
import { ExtractedBillingSchedule } from 'Automations/nodeDefinitions/definitions/action/actionContractExtraction/types'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

type AutomationExecutionTaskStatus =
  Dashboardv99990101Api.Automations.AutomationExecutionTaskStatus

export const ReviewContractExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  reviewableTasks,
  automationExecution,
  isLatestNodeExecution
}) => {
  const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)
  const {
    contractManualReviewTask,
    visibleReviewers,
    reviewableTask,
    completedBy,
    billingSchedule,
    customer
  } = useReviewContractData(tasks, reviewableTasks)

  return (
    <>
      <NodeTimeline.Item
        icon={getManualReviewIcon(
          contractManualReviewTask?.status as AutomationExecutionTaskStatus
        )}
        showStem={!isLatestNodeExecution}
      >
        <NodeTimeline.Title>
          {getManualReviewLabel({
            reviewer: completedBy,
            allReviewers: visibleReviewers,
            startTime: contractManualReviewTask?.startTime || '',
            status: contractManualReviewTask?.status ?? 'IN_PROGRESS'
          })}
        </NodeTimeline.Title>
        {showPreviews && billingSchedule && (
          <ContractPricingPreview
            billingSchedule={billingSchedule}
            fileName={automationExecution.input.fileName ?? 'Unknown file'}
            customer={customer}
            showPhaseBar
            startCollapsed={!isLatestNodeExecution && !reviewableTask}
            action={
              reviewableTask ? (
                <Button
                  onClick={() => {
                    setProcessorTaskId(reviewableTask.id)
                  }}
                  variant="primary"
                  fullWidth
                  style={{
                    textDecoration: 'none'
                  }}
                >
                  Review schedule
                </Button>
              ) : undefined
            }
          />
        )}
      </NodeTimeline.Item>
    </>
  )
}

const getManualReviewIcon = (
  outcome: AutomationExecutionTaskStatus | undefined
) =>
  match(outcome)
    .with('COMPLETED', () => <IconCheckmark />)
    .with(
      'FAILED',
      'FAILED_WITH_TERMINAL_ERROR',
      'TIMED_OUT',
      'CANCELED',
      () => <IconXMark />
    )
    .otherwise(() => <IconShieldCheck />)

const getManualReviewLabel = ({
  reviewer,
  allReviewers,
  startTime,
  status
}: {
  reviewer: string | undefined
  allReviewers: Array<string>
  startTime: string
  status: AutomationExecutionTaskStatus
}) =>
  match(status)
    .with('COMPLETED', () => (
      <NodeTimeline.UserAction
        label="Schedule reviewed"
        performedBy={reviewer}
        timestamp={startTime}
      />
    ))
    .with('FAILED', 'FAILED_WITH_TERMINAL_ERROR', 'TIMED_OUT', () => (
      <NodeTimeline.UserAction
        label="Schedule review failed"
        timestamp={startTime}
      />
    ))
    .with('CANCELED', () => (
      <NodeTimeline.UserAction
        label="Schedule review cancelled"
        timestamp={startTime}
      />
    ))
    .otherwise(() => (
      <NodeTimeline.UserAction
        label="Schedule ready to review"
        performedBy={allReviewers}
        timestamp={startTime}
      />
    ))

const useReviewContractData = (
  tasks: ExecutionSummaryComponentProps['tasks'],
  reviewableTasks: ExecutionSummaryComponentProps['reviewableTasks']
) => {
  const { users } = useAllUsers()
  const { email: currentUserEmail } = useCurrentUser()

  const contractManualReviewTask = useMemo(() => {
    return tasks.find(
      task =>
        task.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.reviewContract
    )
  }, [tasks])

  const { allReviewers, completedBy } = useMemo(() => {
    const assignedUserIds = (
      contractManualReviewTask?.inputData.data.assignmentRules as {
        users: Array<string>
      }
    )?.users

    return {
      allReviewers: resolveUserEmails(assignedUserIds, users),
      completedBy: (contractManualReviewTask?.outputData?.completedBy &&
        users.find(
          user => user.id === contractManualReviewTask?.outputData?.completedBy
        )?.email) as string | undefined
    }
  }, [contractManualReviewTask, users])

  const reviewableTask = useMemo(
    () => findReviewableTask(tasks, reviewableTasks),
    [tasks, reviewableTasks]
  )

  const visibleReviewers = useMemo(
    () =>
      reviewableTask
        ? allReviewers
        : allReviewers.filter(email => email !== currentUserEmail),
    [allReviewers, currentUserEmail, reviewableTask]
  )

  const billingSchedule = useMemo(() => {
    return contractManualReviewTask?.inputData.data
      .billingSchedule as ExtractedBillingSchedule
  }, [contractManualReviewTask])

  const customer = useMemo(() => {
    const customer = contractManualReviewTask?.inputData.data.customer as
      | { legalName?: string }
      | undefined
    return customer
  }, [contractManualReviewTask])

  return {
    contractManualReviewTask,
    visibleReviewers,
    reviewableTask,
    completedBy,
    billingSchedule,
    customer
  }
}
